import useStore from "../../../../Store/Store";
import { logger } from "../../../../utils/Logger";
import { v4 as uuidv4 } from "uuid";
import { SimulationPathTreeBuilder } from "./SimulationPathTreeBuilder";
import { SimulationParentCellInserter } from "./SimulationParentCellInserter";

// Configurable buffer time between processing steps (in milliseconds)
export const STEP_PROCESSING_BUFFER_MS = 500;

// Track if generation is in progress
let generationAbortController: AbortController | null = null;

// Track function calls for debugging
let generationCallCount = 0;

// Global execution guard to prevent multiple concurrent executions
const activeGenerations = new Set<string>();

/**
 * Check if a simulation needs diagram generation
 * Returns true if more than 70% of steps don't have diagram elements
 */
export function needsDiagramGeneration(simulation: Simulation): boolean {
  if (!simulation?.simSteps || simulation.simSteps.length === 0) {
    return false;
  }

  const stepsWithoutDiagrams = simulation.simSteps.filter(
    (step) => !step.diagramNodeId || step.diagramNodeId === ""
  ).length;

  const percentage = (stepsWithoutDiagrams / simulation.simSteps.length) * 100;
  return percentage > 70;
}

/**
 * Abort any ongoing diagram generation
 */
export function abortDiagramGeneration() {
  if (generationAbortController) {
    generationAbortController.abort();
    generationAbortController = null;
    logger.debug.info(
      "Diagram generation aborted - clearing active generations"
    );

    // Clear all active generations when manually aborting
    activeGenerations.clear();
  }

  // NEW: Also abort parent cell insertion
  SimulationParentCellInserter.abort();

  // Hide cell insertion overlay when aborting
  const abortState = useStore.getState();
  abortState.setIsInsertingSimulationCells(false);
}

/**
 * Generate diagram cells for simulation steps
 */
export async function generateDiagramFromSimSteps(
  simulation: Simulation
): Promise<void> {
  // Track function calls
  generationCallCount++;
  logger.debug.info(
    `generateDiagramFromSimSteps called #${generationCallCount} for simulation: ${simulation.name}`
  );

  // Global execution guard - prevent multiple concurrent executions for the same simulation
  if (activeGenerations.has(simulation.name)) {
    logger.warn(
      `Generation already in progress for simulation: ${simulation.name}. Call #${generationCallCount} blocked.`
    );
    return;
  }

  // Add this simulation to active generations
  activeGenerations.add(simulation.name);
  logger.debug.info(
    `Starting diagram generation for simulation: ${simulation.name}`
  );

  // Abort any previous generation
  abortDiagramGeneration();

  // Create new abort controller
  generationAbortController = new AbortController();
  const signal = generationAbortController.signal;

  // Check if this simulation should even need diagram generation
  const shouldGenerate = needsDiagramGeneration(simulation);
  if (!shouldGenerate) {
    logger.warn(
      `needsDiagramGeneration returned false but function was still called! Aborting.`
    );
    // Remove from active generations and exit early
    activeGenerations.delete(simulation.name);
    return;
  }

  // Double-check with the current simulation state in store to get the most up-to-date data
  const currentState = useStore.getState();
  const currentSimulation =
    currentState.simulationsState.simulations[simulation.name];

  if (currentSimulation) {
    const updatedShouldGenerate = needsDiagramGeneration(currentSimulation);

    if (!updatedShouldGenerate) {
      logger.debug.info(
        `Store data shows no generation needed - exiting early`
      );
      activeGenerations.delete(simulation.name);
      return;
    }

    // Use the more up-to-date simulation from store
    simulation = { ...currentSimulation };
  }

  try {
    const simSteps = simulation.simSteps;
    const cellIdMap = new Map<string, string>(); // Map simStepId to cellId
    const edgeSteps: Array<{ step: SimStep; index: number }> = [];

    // NEW: Show cell insertion overlay before starting
    const state = useStore.getState();
    state.setIsInsertingSimulationCells(true);

    // NEW: Insert parent cells BEFORE creating simstep cells
    logger.debug.info("Inserting parent cells for simulation steps...");
    const parentCellMap = await SimulationParentCellInserter.insertParentCells(
      simSteps,
      (current, total) => {
        logger.debug.info(
          `Parent cell insertion progress: ${current}/${total}`
        );
      }
    );

    // Check for abort after parent cell insertion
    if (signal.aborted) {
      logger.debug.info(
        "Diagram generation was aborted after parent cell insertion"
      );
      return;
    }

    // First pass: Process all non-edge steps
    for (let i = 0; i < simSteps.length; i++) {
      // Check for abort signal
      if (signal.aborted) {
        logger.debug.info("Diagram generation was aborted");
        return;
      }

      const currentStep = simSteps[i];

      // Skip if already has diagram node
      if (currentStep.diagramNodeId && currentStep.diagramNodeId !== "") {
        cellIdMap.set(currentStep.simStepId, currentStep.diagramNodeId);
        continue;
      }

      if (currentStep.isEdge === 1) {
        // Store edge steps for later processing
        edgeSteps.push({ step: currentStep, index: i });
      } else {
        // Process non-edge step with individual error handling
        try {
          await processNodeStep(currentStep, simulation, cellIdMap);
        } catch (error) {
          logger.error(
            `Failed to process node step ${currentStep.simStepLabel}:`,
            error
          );
          // Continue with next step instead of stopping
        }

        // Wait buffer time between steps
        await new Promise((resolve) =>
          setTimeout(resolve, STEP_PROCESSING_BUFFER_MS)
        );
      }
    }

    // Second pass: Process all edge steps
    for (const { step: edgeStep, index } of edgeSteps) {
      // Check for abort signal
      if (signal.aborted) {
        logger.debug.info(
          "Diagram generation was aborted during edge processing"
        );
        return;
      }

      try {
        // Find source and target steps more intelligently
        // Look backwards for the nearest non-edge step as source
        let sourceStep = null;
        for (let i = index - 1; i >= 0; i--) {
          if (simSteps[i].isEdge !== 1) {
            sourceStep = simSteps[i];
            break;
          }
        }

        // Look forwards for the nearest non-edge step as target
        let targetStep = null;
        for (let i = index + 1; i < simSteps.length; i++) {
          if (simSteps[i].isEdge !== 1) {
            targetStep = simSteps[i];
            break;
          }
        }

        if (sourceStep && targetStep) {
          // Ensure both source and target have been processed (have cellIds)
          const sourceCellId =
            cellIdMap.get(sourceStep.simStepId) || sourceStep.diagramNodeId;
          const targetCellId =
            cellIdMap.get(targetStep.simStepId) || targetStep.diagramNodeId;

          if (sourceCellId && targetCellId) {
            await processEdgeStep(
              edgeStep,
              sourceStep,
              targetStep,
              simulation,
              cellIdMap
            );
          } else {
            logger.error(
              `Cannot create edge ${edgeStep.simStepLabel}: source or target step not yet processed`,
              {
                sourceHasCellId: !!sourceCellId,
                targetHasCellId: !!targetCellId,
              }
            );
          }
        } else {
          logger.warn(
            `Cannot create edge for step ${edgeStep.simStepLabel}: unable to find valid source or target`,
            { sourceFound: !!sourceStep, targetFound: !!targetStep }
          );
        }
      } catch (error) {
        logger.error(
          `Failed to process edge step ${edgeStep.simStepLabel}:`,
          error
        );
        // Continue with next edge instead of stopping
      }

      // Wait buffer time between edges
      await new Promise((resolve) =>
        setTimeout(resolve, STEP_PROCESSING_BUFFER_MS)
      );
    }

    logger.debug.info(
      `Diagram generation completed for simulation: ${
        simulation.name
      }. Processed ${simSteps.length - edgeSteps.length} nodes and ${
        edgeSteps.length
      } edges.`
    );

    // Force UI refresh to ensure SimSteps reflect updated diagramNodeIds
    const currentState = useStore.getState();

    // Trigger a state update to force UI re-render
    await currentState.setCurrentUnsavedSimulationObject({
      ...currentState.currentUnsavedSimulationObject,
    });
  } catch (error) {
    if (error.name === "AbortError") {
      logger.debug.info("Diagram generation was aborted");
    } else {
      logger.error("Critical error during diagram generation:", error);
    }
  } finally {
    generationAbortController = null;

    // Hide cell insertion overlay
    const finalState = useStore.getState();
    finalState.setIsInsertingSimulationCells(false);

    // Remove simulation from active generations
    activeGenerations.delete(simulation.name);
    logger.debug.info(
      `Diagram generation cleanup completed for ${simulation.name}`
    );
  }
}

/**
 * Process a node (non-edge) simulation step
 */
async function processNodeStep(
  step: SimStep,
  simulation: Simulation,
  cellIdMap: Map<string, string>
): Promise<void> {
  const state = useStore.getState();

  // Check if this step already has a diagram node (prevent duplicates)
  // Also check the current simulation state in the store for the most up-to-date data
  const currentState = useStore.getState();
  const currentSimulation =
    currentState.simulationsState.simulations[simulation.name];
  const storeStep = currentSimulation?.simSteps.find(
    (s) => s.simStepId === step.simStepId
  );

  const effectiveDiagramNodeId = storeStep?.diagramNodeId || step.diagramNodeId;

  if (effectiveDiagramNodeId && effectiveDiagramNodeId !== "") {
    logger.debug.info(
      `Step ${step.simStepLabel} already has diagramNodeId: ${effectiveDiagramNodeId}, skipping creation`
    );
    cellIdMap.set(step.simStepId, effectiveDiagramNodeId);
    return;
  }

  // Check if we already processed this step in the current session
  if (cellIdMap.has(step.simStepId)) {
    logger.debug.info(
      `Step ${step.simStepLabel} already processed in current session, skipping creation`
    );
    return;
  }

  // Generate cell ID
  const cellId = uuidv4();

  // Create cell name based on source code mapping
  let cellName = step.simStepLabel;
  if (step.sourceCodeMapping) {
    const { filePath, startLine, endLine } = step.sourceCodeMapping;
    const fileName = filePath.split("/").pop() || filePath;

    // Convert string line numbers to numbers for comparison
    const startLineNum = parseInt(startLine, 10);
    const endLineNum = parseInt(endLine, 10);

    if (startLineNum === endLineNum) {
      cellName = `${step.simStepLabel} - ${fileName}:L${startLine}`;
    } else {
      cellName = `${step.simStepLabel} - ${fileName}:L${startLine}-${endLine}`;
    }
  }

  // Get parent cell if the code is linked to a file
  let parentCellId = null;
  let parentPath = null;

  if (step.sourceCodeMapping) {
    // Find the parent file in repoData
    const repoData = state.repoData;

    // NEW: First check if the exact file path exists
    const fileData = repoData[step.sourceCodeMapping.filePath];
    if (fileData?.cellId) {
      parentCellId = fileData.cellId;
      parentPath = fileData.path;
    } else {
      // Fallback to existing logic for backward compatibility
      for (const [path, data] of Object.entries(repoData)) {
        if (
          data.type === "blob" &&
          data.path === step.sourceCodeMapping.filePath
        ) {
          parentCellId = data.cellId;
          parentPath = data.path;
          break;
        }
      }
    }
  }

  // Check for abort signal before creating cell
  if (generationAbortController?.signal?.aborted) {
    logger.debug.info(
      `Diagram generation aborted, skipping cell creation for ${step.simStepLabel}`
    );
    return;
  }

  // Create the cell in drawio
  try {
    const response = await state.postToDrawioWaitForResponse({
      action: "NEW_LINKED_CELL",
      data: {
        newCellName: cellName,
        parentCellId: parentCellId,
        cellId: cellId,
      },
    });

    if (response?.status === "SUCCESS") {
      const newCellId = response.data?.newCellId || cellId;

      // IMPORTANT: Create repoData entry for ALL cells, not just those with source code mapping
      // This is required for the "Cell not found in diagram" logic to work correctly

      // Create deterministic key to avoid duplicates - use simStepId for consistency
      const childKey =
        step.sourceCodeMapping && parentPath
          ? `${parentPath}-simstep-${step.simStepId}`
          : `generated-simstep-${step.simStepId}-${newCellId}`;

      const newRepoObject = {
        cellName: cellName,
        cellId: newCellId,
        fileName: step.sourceCodeMapping
          ? step.sourceCodeMapping.filePath.split("/").pop()
          : undefined,
        parentPath: parentPath || undefined,
        parentCellId: parentCellId || undefined,
        path: childKey,
        startLine: step.sourceCodeMapping
          ? parseInt(step.sourceCodeMapping.startLine, 10)
          : undefined,
        endLine: step.sourceCodeMapping
          ? parseInt(step.sourceCodeMapping.endLine, 10)
          : undefined,
        visible: true,
      };

      // Check if repoData entry already exists to prevent overwrites
      const existingRepoData = state.repoData[childKey];
      const existingCellToPath = state.cellToPath[newCellId];

      if (existingRepoData || existingCellToPath) {
        logger.warn(
          `RepoData or CellToPath entry already exists for ${childKey}/${newCellId}, skipping creation to prevent duplicates`
        );
      } else {
        // Create repoData entry and cellToPath mapping
        state.setNewRepoObject(childKey, newRepoObject);
        state.setNewCellToPath(newCellId, childKey);
      }

      // Verify the entries were created
      const verifyRepoData = state.repoData[childKey];
      const verifyCellToPath = state.cellToPath[newCellId];
      logger.debug.info(
        `Created cell for ${
          step.simStepLabel
        }: repoData exists: ${!!verifyRepoData}, cellToPath exists: ${!!verifyCellToPath}`
      );

      // Update parent's children if we have a parent
      if (step.sourceCodeMapping && parentPath) {
        const parentData = state.repoData[parentPath];
        if (parentData) {
          const children = parentData.children || [];
          state.setRepoObjectAttribute(parentPath, "children", [
            ...children,
            childKey,
          ]);
        }
      }

      // Update the simulation step with the diagram node ID
      await updateSimStepDiagramNodeId(simulation, step.simStepId, newCellId);

      // Store in map for edge processing
      cellIdMap.set(step.simStepId, newCellId);
    } else {
      logger.error(
        `Failed to create cell for step ${step.simStepLabel}. Response:`,
        response
      );
    }
  } catch (error) {
    logger.error(`Error in processNodeStep for ${step.simStepLabel}:`, error);
    throw error;
  }
}

/**
 * Process an edge simulation step
 */
async function processEdgeStep(
  edgeStep: SimStep,
  sourceStep: SimStep,
  targetStep: SimStep,
  simulation: Simulation,
  cellIdMap: Map<string, string>
): Promise<void> {
  const state = useStore.getState();

  // Check if this edge step already has a diagram node (prevent duplicates)
  // Also check the current simulation state in the store for the most up-to-date data
  const currentState = useStore.getState();
  const currentSimulation =
    currentState.simulationsState.simulations[simulation.name];
  const storeEdgeStep = currentSimulation?.simSteps.find(
    (s) => s.simStepId === edgeStep.simStepId
  );

  const effectiveEdgeDiagramNodeId =
    storeEdgeStep?.diagramNodeId || edgeStep.diagramNodeId;

  if (effectiveEdgeDiagramNodeId && effectiveEdgeDiagramNodeId !== "") {
    logger.debug.info(
      `Edge step ${edgeStep.simStepLabel} already has diagramNodeId: ${effectiveEdgeDiagramNodeId}, skipping creation`
    );
    cellIdMap.set(edgeStep.simStepId, effectiveEdgeDiagramNodeId);
    return;
  }

  // Check if we already processed this edge step in the current session
  if (cellIdMap.has(edgeStep.simStepId)) {
    logger.debug.info(
      `Edge step ${edgeStep.simStepLabel} already processed in current session, skipping creation`
    );
    return;
  }

  // Get source and target cell IDs
  const sourceCellId =
    cellIdMap.get(sourceStep.simStepId) || sourceStep.diagramNodeId;
  const targetCellId =
    cellIdMap.get(targetStep.simStepId) || targetStep.diagramNodeId;

  if (!sourceCellId || !targetCellId) {
    logger.error(
      `Cannot create edge: missing source (${sourceCellId}) or target (${targetCellId}) cell ID`
    );
    logger.error(`Source step diagramNodeId: ${sourceStep?.diagramNodeId}`);
    logger.error(`Target step diagramNodeId: ${targetStep?.diagramNodeId}`);
    logger.error(`CellIdMap contents:`, Array.from(cellIdMap.entries()));
    return;
  }

  // Validate that source and target are different
  if (sourceCellId === targetCellId) {
    logger.error("Cannot create edge: source and target are the same cell");
    return;
  }

  // Generate edge ID
  const edgeId = uuidv4();

  // Check for abort signal before creating edge
  if (generationAbortController?.signal?.aborted) {
    logger.debug.info(
      `Diagram generation aborted, skipping edge creation for ${edgeStep.simStepLabel}`
    );
    return;
  }

  try {
    // Create edge in drawio
    const response = await state.postToDrawioWaitForResponse({
      action: "INSERT_EDGE",
      data: {
        id: edgeId,
        value: edgeStep.simStepLabel || "",
        source: sourceCellId,
        target: targetCellId,
        style: "endArrow=classic;html=1;",
      },
    });

    if (response?.status === "SUCCESS") {
      const newEdgeId = response.data?.edgeId || edgeId;

      // Create repoData entry for the edge (similar to nodes)
      // Use deterministic key to avoid duplicates
      const edgeKey = `generated-edge-simstep-${edgeStep.simStepId}-${newEdgeId}`;
      const edgeRepoObject = {
        cellName: edgeStep.simStepLabel || `Edge ${newEdgeId}`,
        cellId: newEdgeId,
        fileName: undefined,
        parentPath: undefined,
        parentCellId: undefined,
        path: edgeKey,
        startLine: undefined,
        endLine: undefined,
        visible: true,
      };

      // Check if edge repoData entry already exists to prevent overwrites
      const existingEdgeRepoData = state.repoData[edgeKey];
      const existingEdgeCellToPath = state.cellToPath[newEdgeId];

      if (existingEdgeRepoData || existingEdgeCellToPath) {
        logger.warn(
          `Edge RepoData or CellToPath entry already exists for ${edgeKey}/${newEdgeId}, skipping creation to prevent duplicates`
        );
      } else {
        // Create repoData entry and cellToPath mapping for the edge
        state.setNewRepoObject(edgeKey, edgeRepoObject);
        state.setNewCellToPath(newEdgeId, edgeKey);
      }

      // Verify the edge entries were created
      const verifyEdgeRepoData = state.repoData[edgeKey];
      const verifyEdgeCellToPath = state.cellToPath[newEdgeId];
      logger.debug.info(
        `Created edge for ${
          edgeStep.simStepLabel
        }: repoData exists: ${!!verifyEdgeRepoData}, cellToPath exists: ${!!verifyEdgeCellToPath}`
      );

      // Update the simulation step with the diagram node ID
      await updateSimStepDiagramNodeId(
        simulation,
        edgeStep.simStepId,
        newEdgeId
      );

      // Store in map
      cellIdMap.set(edgeStep.simStepId, newEdgeId);
    } else {
      logger.error(
        `Failed to create edge for step ${edgeStep.simStepLabel}. Response:`,
        response
      );
    }
  } catch (error) {
    logger.error(
      `Error in processEdgeStep for ${edgeStep.simStepLabel}:`,
      error
    );
    throw error;
  }
}

/**
 * Update a simulation step's diagramNodeId
 */
async function updateSimStepDiagramNodeId(
  simulation: Simulation,
  simStepId: string,
  diagramNodeId: string
): Promise<void> {
  const state = useStore.getState();

  logger.debug.info(
    `Updating simStep ${simStepId} with diagramNodeId: ${diagramNodeId}`
  );

  // Update in stored simulations first (this is the source of truth)
  const simulations = state.simulationsState.simulations;
  if (simulations[simulation.name]) {
    const existingEntry =
      simulations[simulation.name].simulationNodesAndEdges[diagramNodeId];

    // Prevent duplicate simStepIds in stored simulations
    const existingSimStepIds = existingEntry ? existingEntry.simStepIds : [];
    const newSimStepIds = existingSimStepIds.includes(simStepId)
      ? existingSimStepIds
      : [...existingSimStepIds, simStepId];

    const updatedStoredSimulation = {
      ...simulations[simulation.name],
      simSteps: simulations[simulation.name].simSteps.map((step) =>
        step.simStepId === simStepId ? { ...step, diagramNodeId } : step
      ),
      simulationNodesAndEdges: {
        ...simulations[simulation.name].simulationNodesAndEdges,
        [diagramNodeId]: {
          simStepIds: newSimStepIds,
        },
      },
    };

    await state.setSimulations({
      ...simulations,
      [simulation.name]: updatedStoredSimulation,
    });
  } else {
    logger.error(
      `Stored simulation ${simulation.name} not found in simulations state`
    );
  }

  // Update in current unsaved simulation if it's the same AND has simSteps
  if (state.currentUnsavedSimulationObject?.name === simulation.name) {
    const currentSimSteps = state.currentUnsavedSimulationObject.simSteps || [];

    if (currentSimSteps.length === 0) {
      logger.warn(
        `currentUnsavedSimulationObject has empty simSteps, syncing from stored simulation`
      );

      // Sync currentUnsavedSimulationObject with the updated stored simulation
      const updatedStoredSim =
        useStore.getState().simulationsState.simulations[simulation.name];
      if (updatedStoredSim) {
        await state.setCurrentUnsavedSimulationObject(updatedStoredSim);
      }
    } else {
      // Find the step to update
      const stepToUpdate = currentSimSteps.find(
        (step) => step.simStepId === simStepId
      );

      if (!stepToUpdate) {
        logger.error(
          `SimStep with ID ${simStepId} not found in currentUnsavedSimulationObject, but simSteps length is ${currentSimSteps.length}`
        );

        // Fallback: sync with stored simulation
        const updatedStoredSim =
          useStore.getState().simulationsState.simulations[simulation.name];
        if (updatedStoredSim) {
          await state.setCurrentUnsavedSimulationObject(updatedStoredSim);
        }
        return;
      }

      const updatedSimSteps = currentSimSteps.map((step) =>
        step.simStepId === simStepId ? { ...step, diagramNodeId } : step
      );

      // Handle existing simulationNodesAndEdges entry
      const existingEntry =
        state.currentUnsavedSimulationObject.simulationNodesAndEdges[
          diagramNodeId
        ];

      // Prevent duplicate simStepIds in simulationNodesAndEdges
      const existingSimStepIds = existingEntry ? existingEntry.simStepIds : [];
      const newSimStepIds = existingSimStepIds.includes(simStepId)
        ? existingSimStepIds
        : [...existingSimStepIds, simStepId];

      const updatedSimulationNodesAndEdges = {
        ...state.currentUnsavedSimulationObject.simulationNodesAndEdges,
        [diagramNodeId]: {
          simStepIds: newSimStepIds,
        },
      };

      const updatedUnsavedSimulation = {
        ...state.currentUnsavedSimulationObject,
        simSteps: updatedSimSteps,
        simulationNodesAndEdges: updatedSimulationNodesAndEdges,
      };

      await state.setCurrentUnsavedSimulationObject(updatedUnsavedSimulation);
    }
  } else {
    logger.warn(
      `Current simulation name (${state.currentUnsavedSimulationObject?.name}) does not match target simulation (${simulation.name})`
    );
  }
}
